
import { v4 as uuidv4 } from 'uuid';
import { Node, Edge, MarkerType } from 'reactflow';
import { NodeData } from './types';

export class DiGraph {
  adj: Map<string, string[]> = new Map();

  addVertex(id: string) {
    if (!this.adj.has(id)) this.adj.set(id, []);
  }

  addEdge(from: string, to: string) {
    this.addVertex(from);
    this.addVertex(to);
    const neighbors = this.adj.get(from) || [];
    if (!neighbors.includes(to)) {
      neighbors.push(to);
      this.adj.set(from, neighbors);
    }
  }

  findCycles(): string[][] {
    const cycles: string[][] = [];
    const visited = new Set<string>();
    const recStack = new Set<string>();
    const path: string[] = [];

    const dfs = (v: string) => {
      visited.add(v);
      recStack.add(v);
      path.push(v);

      const neighbors = this.adj.get(v) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          dfs(neighbor);
        } else if (recStack.has(neighbor)) {
          const cycleStartIdx = path.indexOf(neighbor);
          if (cycleStartIdx !== -1) {
            cycles.push(path.slice(cycleStartIdx));
          }
        }
      }

      recStack.delete(v);
      path.pop();
    };

    for (const node of this.adj.keys()) {
      if (!visited.has(node)) {
        dfs(node);
      }
    }

    return cycles;
  }

  getDeepChildren(startId: string): string[] {
    const children = new Set<string>();
    const stack = [startId];
    const visited = new Set<string>();

    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);

      const neighbors = this.adj.get(current) || [];
      for (const n of neighbors) {
        children.add(n);
        stack.push(n);
      }
    }
    children.delete(startId);
    return Array.from(children);
  }
}

export const createEmptyNode = (id: string, position: { x: number, y: number } | null, isRootNode: boolean): Node<NodeData> => {
  return {
    id: id || uuidv4(),
    type: 'group',
    position: position || { x: 0, y: 0 },
    data: {
      name: 'New Group',
      description: '',
      iconPath: 'Layout',
      color: '#0d9488', // Teal-600
      isRootNode,
      inCycle: false,
      isActive: true,
      isDeleted: false,
      isSuspended: false,
      deletionReason: null,
      deletionMessage: null,
      suspensionReason: null,
      suspensionMessage: null,
      attributes: [],
      properties: [],
      memberCount: 0,
      inclusiveMemberCount: 0,
      descendants: []
    }
  };
};

export const groupFromNode = (node: Node<NodeData>, edges: Edge[]) => {
  const children = edges
    .filter(e => e.source === node.id)
    .map(e => e.target);

  return {
    id: node.id,
    name: node.data.name,
    isActive: node.data.isActive,
    isDeleted: node.data.isDeleted,
    isSuspended: node.data.isSuspended,
    deletionReason: node.data.deletionReason,
    deletionMessage: node.data.deletionMessage,
    suspensionReason: node.data.suspensionReason,
    suspensionMessage: node.data.suspensionMessage,
    children,
    attributes: node.data.attributes,
    properties: node.data.properties,
    memberCount: node.data.memberCount,
    // Store diagrammatic data if the backend supports it, otherwise this might be lost on roundtrip
    // depending on the backend schema. For now, we map it back.
    description: node.data.description,
    iconPath: node.data.iconPath
  };
};

// Layout utility to transform hierarchical JSON into React Flow Nodes/Edges
export const transformJsonToGraph = (jsonData: any) => {
  const nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];
  const map: Record<string, any> = {};
  
  // 1. Build Map
  jsonData.nodes.forEach((n: any) => {
    map[n.id] = { ...n, _children: [] };
  });

  // 2. Build Hierarchy Tree from flat list
  let rootId: string | null = null;
  jsonData.nodes.forEach((n: any) => {
    // If parent exists in our map, link it. 
    if (n.parent && map[n.parent]) {
      map[n.parent]._children.push(n.id);
    } else {
      rootId = n.id; 
    }
  });

  // Fallback if multiple roots or disjointed
  if (!rootId && jsonData.rootGroupId) {
    rootId = jsonData.rootGroupId;
  }

  // 2b. Calculate Inclusive Member Counts (Recursive)
  // We use a memoization map to store results to avoid re-calculation
  const inclusiveCounts = new Map<string, number>();

  const getInclusiveCount = (nodeId: string): number => {
    if (inclusiveCounts.has(nodeId)) {
      return inclusiveCounts.get(nodeId)!;
    }

    const node = map[nodeId];
    if (!node) return 0;

    let count = (node.memberCount || 0);
    const children = node._children || [];
    
    for (const childId of children) {
      count += getInclusiveCount(childId);
    }

    inclusiveCounts.set(nodeId, count);
    return count;
  };

  // Pre-calculate all inclusive counts
  jsonData.nodes.forEach((n: any) => {
    getInclusiveCount(n.id);
  });

  // 3. DFS for Layout (Simple Tree Layout)
  const X_SPACING = 300; // Horizontal spacing between levels
  const Y_SPACING = 180; // Vertical spacing between siblings

  const layoutNode = (id: string, depth: number, yOffset: { val: number }) => {
    const nodeData = map[id];
    if (!nodeData) return;

    const currentY = yOffset.val;
    
    // Create Node
    const isRoot = id === rootId;
    
    // Convert JSON Attributes to our Attribute type
    const attributes = nodeData.attributes || [];
    
    nodes.push({
      id: id,
      type: 'group',
      position: { x: depth * X_SPACING, y: currentY },
      data: {
        name: nodeData.name,
        // Removed type injection. Description can come from JSON or be empty.
        description: nodeData.description || '', 
        iconPath: isRoot ? 'Building' : (nodeData.iconPath || 'Users'),
        color: '#0d9488',
        isRootNode: isRoot,
        inCycle: false,
        
        // Map Status Flags
        isActive: nodeData.isActive !== undefined ? nodeData.isActive : true,
        isDeleted: nodeData.isDeleted || false,
        isSuspended: nodeData.isSuspended || false,
        deletionReason: nodeData.deletionReason || null,
        deletionMessage: nodeData.deletionMessage || null,
        suspensionReason: nodeData.suspensionReason || null,
        suspensionMessage: nodeData.suspensionMessage || null,

        attributes: attributes,
        properties: [],
        memberCount: nodeData.memberCount,
        inclusiveMemberCount: inclusiveCounts.get(id),
        descendants: []
      }
    });

    // Create Edges
    if (nodeData.parent && map[nodeData.parent]) {
      edges.push({
        id: `e-${nodeData.parent}-${id}`,
        source: nodeData.parent,
        target: id,
        type: 'base',
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    }

    // Process Children
    const childrenIds = nodeData._children || [];
    
    if (childrenIds.length > 0) {
      childrenIds.forEach((childId: string) => {
        layoutNode(childId, depth + 1, yOffset);
      });
    } else {
      // Leaf node, increment Y for the next node in the traversal
      yOffset.val += Y_SPACING;
    }
  };

  if (rootId) {
    layoutNode(rootId, 0, { val: 0 });
  }

  // Handle disjoint nodes if any (nodes not reached by DFS from root)
  const placedNodeIds = new Set(nodes.map(n => n.id));
  let disjointY = 0;
  jsonData.nodes.forEach((n: any) => {
    if (!placedNodeIds.has(n.id)) {
      nodes.push({
        id: n.id,
        type: 'group',
        position: { x: 0, y: disjointY + 500 }, // Place them below
        data: {
          name: n.name,
          description: 'Detached Node',
          iconPath: 'AlertTriangle',
          color: '#ef4444',
          isRootNode: false,
          inCycle: false,
          isActive: n.isActive !== undefined ? n.isActive : true,
          isDeleted: n.isDeleted || false,
          isSuspended: n.isSuspended || false,
          deletionReason: n.deletionReason || null,
          deletionMessage: n.deletionMessage || null,
          suspensionReason: n.suspensionReason || null,
          suspensionMessage: n.suspensionMessage || null,
          attributes: [],
          properties: [],
          memberCount: n.memberCount,
          inclusiveMemberCount: n.memberCount,
          descendants: []
        }
      });
      disjointY += Y_SPACING;
    }
  });

  return { nodes, edges };
};
